/**
 * Create or edit a gateway profile. Validates equipment types against gateway_register_maps (backend).
 */
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, ChevronDown, Plus, Trash2, AlertCircle } from 'lucide-react';
import { useGatewayRegisterMapTypes } from '@/hooks/useGatewayRegisterMapTypes';
import { useCreateGatewayProfile, useUpdateGatewayProfile } from '@/hooks/useGatewayProfiles';
import type { GatewayProfile, GatewayProfileExpectedDevice, GatewayProfilePropertyDefaults } from '@/types/gateway';
import type { CreateGatewayProfileInput } from '@/services/gateway';

const OTA_OPTIONS = [
  { value: 'stable', label: 'Stable' },
  { value: 'pilot', label: 'Pilot' },
];

const PROTOCOL_OPTIONS = [
  { value: 'modbus_rtu', label: 'Modbus RTU' },
  { value: 'can', label: 'CAN' },
];

const GRID_MODE_OPTIONS = [
  { value: 'off_grid', label: 'Off-grid' },
  { value: 'grid_tied', label: 'Grid-tied' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'grid_primary', label: 'Grid primary' },
];

const emptyDevice = (): GatewayProfileExpectedDevice => ({
  bus_address: 1,
  protocol: 'modbus_rtu',
  equipment_type: '',
  display_name: '',
});

export interface GatewayProfileFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: GatewayProfile | null; // null = create
  onSaved?: () => void;
}

export function GatewayProfileFormDialog({
  open,
  onOpenChange,
  profile,
  onSaved,
}: GatewayProfileFormDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [pollIntervalMs, setPollIntervalMs] = useState(5000);
  const [uploadIntervalMs, setUploadIntervalMs] = useState(30000);
  const [heartbeatIntervalMs, setHeartbeatIntervalMs] = useState(60000);
  const [commandPollIntervalMs, setCommandPollIntervalMs] = useState(10000);
  const [writesEnabled, setWritesEnabled] = useState(false);
  const [otaChannel, setOtaChannel] = useState<'pilot' | 'stable'>('stable');
  const [expectedDevices, setExpectedDevices] = useState<GatewayProfileExpectedDevice[]>([]);
  const [propertyDefaultsOpen, setPropertyDefaultsOpen] = useState(false);
  const [gridMode, setGridMode] = useState<string>('');
  const [batteryCapacityKwh, setBatteryCapacityKwh] = useState<number | ''>('');
  const [batteryReservePct, setBatteryReservePct] = useState<number | ''>('');
  const [autoCommission, setAutoCommission] = useState(false);
  const [autoCreateProperty, setAutoCreateProperty] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const { equipmentTypes, isLoading: typesLoading } = useGatewayRegisterMapTypes();
  const createMutation = useCreateGatewayProfile();
  const updateMutation = useUpdateGatewayProfile();

  useEffect(() => {
    if (!open) return;
    setErrorMsg('');
    if (profile) {
      setName(profile.name);
      setDescription(profile.description ?? '');
      setPollIntervalMs(profile.poll_interval_ms);
      setUploadIntervalMs(profile.upload_interval_ms);
      setHeartbeatIntervalMs(profile.heartbeat_interval_ms);
      setCommandPollIntervalMs(profile.command_poll_interval_ms);
      setWritesEnabled(profile.writes_enabled);
      setOtaChannel(profile.ota_channel);
      setExpectedDevices(
        profile.expected_devices?.length
          ? profile.expected_devices.map((d) => ({ ...d, display_name: d.display_name ?? '' }))
          : [emptyDevice()]
      );
      const def = profile.default_property_config;
      setGridMode(def?.grid_mode ?? '');
      setBatteryCapacityKwh(def?.battery_capacity_kwh ?? '');
      setBatteryReservePct(def?.battery_reserve_pct ?? '');
      setAutoCommission(profile.auto_commission);
      setAutoCreateProperty(profile.auto_create_property);
      setIsDefault(profile.is_default);
    } else {
      setName('');
      setDescription('');
      setPollIntervalMs(5000);
      setUploadIntervalMs(30000);
      setHeartbeatIntervalMs(60000);
      setCommandPollIntervalMs(10000);
      setWritesEnabled(false);
      setOtaChannel('stable');
      setExpectedDevices([emptyDevice()]);
      setGridMode('');
      setBatteryCapacityKwh('');
      setBatteryReservePct('');
      setAutoCommission(false);
      setAutoCreateProperty(false);
      setIsDefault(false);
    }
  }, [open, profile]);

  const addDevice = () => {
    const nextAddr =
      expectedDevices.length === 0
        ? 1
        : Math.max(...expectedDevices.map((d) => d.bus_address), 0) + 1;
    setExpectedDevices([...expectedDevices, { ...emptyDevice(), bus_address: nextAddr }]);
  };

  const removeDevice = (index: number) => {
    if (expectedDevices.length <= 1) return;
    setExpectedDevices(expectedDevices.filter((_, i) => i !== index));
  };

  const updateDevice = (index: number, field: keyof GatewayProfileExpectedDevice, value: string | number) => {
    const next = [...expectedDevices];
    (next[index] as Record<string, unknown>)[field] = value;
    setExpectedDevices(next);
  };

  const defaultPropertyConfig: GatewayProfilePropertyDefaults | null =
    gridMode || batteryCapacityKwh !== '' || batteryReservePct !== ''
      ? {
          ...(gridMode && { grid_mode: gridMode as GatewayProfilePropertyDefaults['grid_mode'] }),
          ...(batteryCapacityKwh !== '' && { battery_capacity_kwh: Number(batteryCapacityKwh) }),
          ...(batteryReservePct !== '' && { battery_reserve_pct: Number(batteryReservePct) }),
        }
      : null;

  const handleSubmit = async () => {
    setErrorMsg('');
    const trimmedName = name.trim();
    if (!trimmedName) {
      setErrorMsg('Name is required');
      return;
    }
    const devices = expectedDevices
      .map((d) => ({
        bus_address: Number(d.bus_address),
        protocol: d.protocol,
        equipment_type: (d.equipment_type || '').trim(),
        display_name: (d.display_name || '').trim() || undefined,
      }))
      .filter((d) => d.equipment_type);
    if (devices.length === 0) {
      setErrorMsg('Add at least one expected device with an equipment type');
      return;
    }

    const input: CreateGatewayProfileInput = {
      name: trimmedName,
      description: description.trim() || null,
      poll_interval_ms: pollIntervalMs,
      upload_interval_ms: uploadIntervalMs,
      heartbeat_interval_ms: heartbeatIntervalMs,
      command_poll_interval_ms: commandPollIntervalMs,
      writes_enabled: writesEnabled,
      ota_channel: otaChannel,
      expected_devices: devices,
      default_property_config: defaultPropertyConfig,
      auto_commission: autoCommission,
      auto_create_property: autoCreateProperty,
      is_default: isDefault,
    };

    try {
      if (profile) {
        await updateMutation.mutateAsync({ profileId: profile.id, input });
      } else {
        await createMutation.mutateAsync(input);
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : 'Failed to save profile');
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl bg-wt-bg-secondary border-border max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-wt-text-primary">
            {profile ? 'Edit profile' : 'Create profile'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-wt-text-secondary">Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Standard Sol-Ark 15K"
                className="bg-wt-bg-tertiary border-border mt-1"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-wt-text-secondary">Description (optional)</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Short description"
                className="bg-wt-bg-tertiary border-border mt-1"
              />
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-wt-text-primary mb-2">Gateway config</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-wt-text-secondary text-xs">Poll interval (ms)</Label>
                <Input
                  type="number"
                  min={1000}
                  value={pollIntervalMs}
                  onChange={(e) => setPollIntervalMs(Number(e.target.value) || 5000)}
                  className="bg-wt-bg-tertiary border-border mt-1 font-mono-numeric"
                />
              </div>
              <div>
                <Label className="text-wt-text-secondary text-xs">Upload interval (ms)</Label>
                <Input
                  type="number"
                  min={5000}
                  value={uploadIntervalMs}
                  onChange={(e) => setUploadIntervalMs(Number(e.target.value) || 30000)}
                  className="bg-wt-bg-tertiary border-border mt-1 font-mono-numeric"
                />
              </div>
              <div>
                <Label className="text-wt-text-secondary text-xs">Heartbeat interval (ms)</Label>
                <Input
                  type="number"
                  min={10000}
                  value={heartbeatIntervalMs}
                  onChange={(e) => setHeartbeatIntervalMs(Number(e.target.value) || 60000)}
                  className="bg-wt-bg-tertiary border-border mt-1 font-mono-numeric"
                />
              </div>
              <div>
                <Label className="text-wt-text-secondary text-xs">Command poll (ms)</Label>
                <Input
                  type="number"
                  min={5000}
                  value={commandPollIntervalMs}
                  onChange={(e) => setCommandPollIntervalMs(Number(e.target.value) || 10000)}
                  className="bg-wt-bg-tertiary border-border mt-1 font-mono-numeric"
                />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <Switch checked={writesEnabled} onCheckedChange={setWritesEnabled} />
                <Label className="text-wt-text-secondary">Writes enabled</Label>
              </div>
              <div>
                <Label className="text-wt-text-secondary text-xs">OTA channel</Label>
                <Select value={otaChannel} onValueChange={(v) => setOtaChannel(v as 'pilot' | 'stable')}>
                  <SelectTrigger className="bg-wt-bg-tertiary border-border mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OTA_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-wt-text-primary mb-2">Expected devices</p>
            <div className="space-y-2">
              {expectedDevices.map((d, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-wt-bg-tertiary">
                  <Input
                    type="number"
                    min={1}
                    className="w-16 font-mono-numeric"
                    value={d.bus_address}
                    onChange={(e) => updateDevice(i, 'bus_address', Number(e.target.value) || 1)}
                  />
                  <Select
                    value={d.protocol}
                    onValueChange={(v) => updateDevice(i, 'protocol', v)}
                  >
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROTOCOL_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={d.equipment_type || ''}
                    onValueChange={(v) => updateDevice(i, 'equipment_type', v)}
                    disabled={typesLoading}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Equipment type" />
                    </SelectTrigger>
                    <SelectContent>
                      {equipmentTypes.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Display name"
                    className="flex-1 min-w-[100px]"
                    value={d.display_name ?? ''}
                    onChange={(e) => updateDevice(i, 'display_name', e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-wt-text-secondary"
                    onClick={() => removeDevice(i)}
                    disabled={expectedDevices.length <= 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addDevice}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Add device
              </Button>
            </div>
          </div>

          <Collapsible open={propertyDefaultsOpen} onOpenChange={setPropertyDefaultsOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center gap-1 text-wt-text-secondary">
                <ChevronDown className={`w-4 h-4 transition ${propertyDefaultsOpen ? 'rotate-180' : ''}`} />
                Property defaults (optional)
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div>
                  <Label className="text-wt-text-secondary text-xs">Grid mode</Label>
                  <Select value={gridMode || 'none'} onValueChange={(v) => setGridMode(v === 'none' ? '' : v)}>
                    <SelectTrigger className="bg-wt-bg-tertiary border-border mt-1">
                      <SelectValue placeholder="Optional" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {GRID_MODE_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-wt-text-secondary text-xs">Battery capacity (kWh)</Label>
                  <Input
                    type="number"
                    min={0}
                    placeholder="Optional"
                    value={batteryCapacityKwh === '' ? '' : batteryCapacityKwh}
                    onChange={(e) => setBatteryCapacityKwh(e.target.value === '' ? '' : Number(e.target.value))}
                    className="bg-wt-bg-tertiary border-border mt-1 font-mono-numeric"
                  />
                </div>
                <div>
                  <Label className="text-wt-text-secondary text-xs">Reserve (%)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    placeholder="Optional"
                    value={batteryReservePct === '' ? '' : batteryReservePct}
                    onChange={(e) => setBatteryReservePct(e.target.value === '' ? '' : Number(e.target.value))}
                    className="bg-wt-bg-tertiary border-border mt-1 font-mono-numeric"
                  />
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          <div className="space-y-2">
            <p className="text-sm font-medium text-wt-text-primary">Automation</p>
            <div className="flex items-center gap-2">
              <Switch checked={autoCommission} onCheckedChange={setAutoCommission} />
              <Label className="text-wt-text-secondary">Auto-commission (skip manual commissioning)</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={autoCreateProperty} onCheckedChange={setAutoCreateProperty} />
              <Label className="text-wt-text-secondary">Auto-create property (zero-touch)</Label>
            </div>
            {autoCreateProperty && (
              <p className="text-xs text-wt-amber-primary">
                Gateway will automatically create a property record on first connection. You can rename it later.
              </p>
            )}
            <div className="flex items-center gap-2">
              <Switch checked={isDefault} onCheckedChange={setIsDefault} />
              <Label className="text-wt-text-secondary">Set as org default profile</Label>
            </div>
          </div>

          {errorMsg && (
            <div className="flex items-center gap-2 text-sm text-wt-red-primary">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {errorMsg}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving || !name.trim()}>
            {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {profile ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
